import { createRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableContainer } from "@material-ui/core";
import Paper from "paper";
import { Divider, SvgIcon, useTheme } from "@mui/material";
import { width } from "@mui/system";

// 座標入力タブ
type CoordinatePointColumn = {
  id: string;
  pointName: string;
  x: number;
  y: number;
  z: number;
  timeStamp: Date;
};

interface GridProps {
  gridRowCol: CoordinatePointColumn[]; // input
  open: boolean;
  height: string;
}

const getWindowSize = () => {
  const { innerWidth, innerHeight } = window;
  const widthProjectDetail4 =
    document.getElementsByClassName("ProjectDetail4")[0]?.clientWidth || 0;
  const headerHeight = 90;
  return {
    innerWidth: innerWidth - widthProjectDetail4,
    innerHeight: innerHeight - headerHeight,
    widthProjectDetail4,
  };
};

const GridCoordinatePoint = (props: GridProps) => {
  const { t } = useTranslation();
  const [windowSize, setWindowSize] = useState(getWindowSize());
  const theme = useTheme();
  const ref = createRef<HTMLDivElement>();

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize());
    }

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      createShape();
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
  }, [ref.current, props.open]);

  /*****************************************
      state
  ******************************************/

  const drawPoint = (widthCanvas: number, heightCanvas: number) => {
    const minCanvas = Math.min(widthCanvas, heightCanvas);
    var data = props.gridRowCol;
    var minx = Math.min(...data.map((item) => item.x));
    var maxx = Math.max(...data.map((item) => item.x));
    var miny = Math.min(...data.map((item) => item.y));
    var maxy = Math.max(...data.map((item) => item.y));

    if (minx === maxx) {
      minx -= 0.5;
      maxx += 0.5;
    }

    if (miny === maxy) {
      miny -= 0.5;
      maxy += 0.5;
    }

    // キャンバスの原点: 左上
    // 端ギリギリまで使わないためにheight: 0.8, width: 0.6倍する: 横はaxes分あけておく
    var scalex = (heightCanvas / (maxx - minx)) * 0.8;
    var scaley = (widthCanvas / (maxy - miny)) * 0.6;

    // offset計算
    // 左上から0.1ずつ中央へずらすと真ん中にレイアウトできる
    minx = minx - (heightCanvas / scalex) * 0.1;
    miny = miny - (widthCanvas / scaley) * 0.1;

    const radius = minCanvas * 0.015;

    data.forEach((element) => {
      if (theme.palette.mode === "dark") {
        var circle = new Paper.Path.Circle(
          new Paper.Point(
            (element.y - miny) * scaley,
            heightCanvas - (element.x - minx) * scalex
          ),
          radius
        );
        circle.fillColor = new Paper.Color("#ffd966");
      } else if (theme.palette.mode === "light") {
        // lightの際は黒い縁取り追加
        var circleBack = new Paper.Path.Circle(
          new Paper.Point(
            (element.y - miny) * scaley,
            heightCanvas - (element.x - minx) * scalex
          ),
          radius * (4 / 3)
        );
        circleBack.fillColor = new Paper.Color("#000000");

        var circleFront = new Paper.Path.Circle(
          new Paper.Point(
            (element.y - miny) * scaley,
            heightCanvas - (element.x - minx) * scalex
          ),
          radius
        );
        circleFront.fillColor = new Paper.Color("#ffd966");
      }

      var text = new Paper.PointText(
        new Paper.Point(
          (element.y - miny) * scaley + radius,
          heightCanvas - (element.x - minx) * scalex + 7
        )
      );
      text.justification = "top";
      text.fillColor = new Paper.Color(theme.palette.base.contrast);
      text.content = element.pointName;
    });
    drawVector(widthCanvas, heightCanvas);
    Paper.view.play();
  };

  const drawVector = (widthCanvas: number, heightCanvas: number) => {
    // setting
    // 始点: 右上から0.25倍
    const axisCenter = 0.25;
    // 終点: 右上から0.1倍
    const axisPadding = 0.1;
    const textCenter = 0.11;
    const textPadding = 0.03;
    const Size = Math.min(widthCanvas, heightCanvas);

    const arrowHeadx = new Paper.Path.RegularPolygon(
      new Paper.Point(widthCanvas - Size * axisCenter, Size * axisPadding),
      3,
      3
    );
    arrowHeadx.fillColor = new Paper.Color("#FF00FF");

    var vectorItemx = new Paper.Group([
      new Paper.Path([
        new Paper.Point(widthCanvas - Size * axisCenter, Size * axisCenter),
        new Paper.Point(widthCanvas - Size * axisCenter, Size * axisPadding),
      ]),
      arrowHeadx,
    ]);
    var text = new Paper.PointText(
      new Paper.Point(
        widthCanvas - Size * (axisCenter - textPadding),
        Size * textCenter
      )
    );
    text.justification = "bottom";
    text.fillColor = new Paper.Color("#FF00FF");
    text.content = "X";
    text.fontSize = "11px";

    const arrowHeady = new Paper.Path.RegularPolygon(
      new Paper.Point(widthCanvas - Size * axisPadding, Size * axisCenter),
      3,
      3
    );
    arrowHeady.fillColor = new Paper.Color("#00FF00");
    arrowHeady.rotate(90);

    var vectorItemy = new Paper.Group([
      new Paper.Path([
        new Paper.Point(widthCanvas - Size * axisCenter, Size * axisCenter),
        new Paper.Point(widthCanvas - Size * axisPadding, Size * axisCenter),
      ]),
      arrowHeady,
    ]);
    var text = new Paper.PointText(
      new Paper.Point(
        widthCanvas - Size * textCenter,
        Size * (axisCenter - textPadding)
      )
    );
    text.justification = "top";
    text.fillColor = new Paper.Color("#00FF00");
    text.content = "Y";
    text.fontSize = "11px";

    vectorItemx.strokeWidth = 3.0;
    vectorItemx.strokeColor = new Paper.Color("#FF00FF");
    vectorItemy.strokeWidth = 3.0;
    vectorItemy.strokeColor = new Paper.Color("#00FF00");
  };

  const createShape = () => {
    // Instantiate the paperScope with the canvas element
    const myCanvas = document.getElementById(
      "canvas-coordinate"
    ) as HTMLCanvasElement;
    if (!myCanvas) return;
    myCanvas.style.width = "100%";
    myCanvas.style.height = "calc(100% - 4px)";
    if (myCanvas) {
      Paper.setup(myCanvas);

      const dpr = window.devicePixelRatio || 1;

      Paper.view.viewSize = new Paper.Size(
        myCanvas.clientWidth,
        myCanvas.clientHeight
      );
      Paper.view.scale(1.0 / dpr);

      drawPoint(myCanvas.clientWidth, myCanvas.clientHeight);
      Paper.view.onFrame = () => {};
    }
  };

  useEffect(() => {
    createShape();
  }, [
    props.gridRowCol,
    props.open,
    windowSize,
    theme.palette.mode,
    props.height,
  ]);

  return props.open ? (
    <TableContainer
      ref={ref}
      style={{
        backgroundColor: theme.palette.base.main,
        width: props.open ? "auto" : "24px",
        aspectRatio: "2",
        height: "l00%",
        overflow: "unset",
        borderLeft: `1px solid ${theme.palette.base.border}`,
        boxSizing: "content-box",
      }}
    >
      <canvas
        id="canvas-coordinate"
        style={{
          objectFit: "none",
          width: "auto",
          aspectRatio: "2",
        }}
      />
    </TableContainer>
  ) : (
    <></>
  );
};

export default GridCoordinatePoint;
